// src/utils/bccMe.js
// "BCC Me" (Henry, 09/28/2026): a tick box in the email windows that blind copies the person
// clicking Send, so a copy lands in their own inbox as confirmation the email went out.
//
// The one thing this may ever put on Bcc is the sender's OWN address. Clients stay on the visible
// To line (emailFanout.js allOnTo: every client on To, never Bcc), and nothing here reads a client.
// With the box unticked the draft's Bcc is returned exactly as it was built.
//
// The choice is remembered per person in this browser (localStorage). A private window or blocked
// storage simply starts unticked.
import { cleanEmail } from "./cleanEmail";

const KEY_PREFIX = "te.bccMe.";
const keyFor = (userId) => `${KEY_PREFIX}${String(userId || "anon")}`;

export function readBccMe(userId) {
  try {
    return window.localStorage.getItem(keyFor(userId)) === "1";
  } catch {
    return false;
  }
}

export function writeBccMe(userId, on) {
  try {
    if (on) window.localStorage.setItem(keyFor(userId), "1");
    else window.localStorage.removeItem(keyFor(userId));
  } catch {
    /* the box still works for this send; it just is not remembered */
  }
}

const splitList = (value) =>
  String(value || "")
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean);

// The address inside "Name <a@b.com>", or the value itself.
const bareAddress = (s) => {
  const m = /<([^>]+)>/.exec(s);
  return cleanEmail(m ? m[1] : s).toLowerCase();
};

/**
 * The Bcc to send with. Unticked, or no sender address: `bcc` unchanged. Ticked: the sender's own
 * address is added once, unless they already get the email on To, Cc or Bcc.
 * @param {{to?: string, cc?: string, bcc?: string}} draft
 * @param {string} senderEmail  the signed-in person's own address
 * @param {boolean} on          the BCC Me box
 * @returns {string|undefined}
 */
export function withSenderBcc(draft, senderEmail, on) {
  const bcc = draft?.bcc;
  const me = cleanEmail(senderEmail || "");
  if (!on || !me || !me.includes("@")) return bcc;
  const already = [draft?.to, draft?.cc, bcc].flatMap(splitList).map(bareAddress);
  if (already.includes(me.toLowerCase())) return bcc;
  return [...splitList(bcc), me].join(", ");
}
